import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectActiveClickToIdentifyServices,
  getQueryableLayers,
} from '@rsos/reduxDataStore';
import LayerSelectionMenu from './LayerSelectionMenu';
import { showMapClickInfo, hideMapClickInfo } from './clickToIdentifySlice';
import { FEATURE_SERVICE, MAP_SERVICE } from './constants';
import { queryFeatureLayer, identifyMapService } from './layerHelpers';
import type {
  MapPoint,
  MapClickEvent,
  FeatureData,
  MapClickInfo,
  LayerSelectionResult,
  LayerSelectionMenuData,
  RootState,
  ClickToIdentifyGLProps,
  HelperFunctionResult,
} from './types';

const formatMapClickInfo = (
  event: { mapPoint: MapPoint },
  result: Partial<FeatureData>,
): MapClickInfo => {
  return {
    latitude: event.mapPoint.latitude,
    longitude: event.mapPoint.longitude,
    featureData: {
      layerName: result.layerName || '',
      layerId: result.layerId || '',
      attributes: result.attributes || {},
      fields: Object.keys(result.attributes || {}).map(key => ({
        name: key,
        alias: key,
      })),
      parentFeatureLayerID: result.layerId || result.layerName || '',
      isLoading: result.isLoading || false,
    },
  };
};

/**
 * Identify and analyze points on the Esri map
 */
const ClickToIdentifyGL = forwardRef<HTMLElement, ClickToIdentifyGLProps>(
  ({ mapInstance, identifyTolerance = 3, queryBufferMeters = 3 }, ref) => {
    const dispatch = useDispatch();
    const mapRef = typeof ref === 'object' && ref?.current ? ref.current : null;
    const view = mapInstance?.view;

    const activeLayers = useSelector(selectActiveClickToIdentifyServices);
    const mapClickInfo = useSelector(
      (state: RootState) => state.clickToIdentify?.mapClickInfo,
    );

    const [layerSelectionMenu, setLayerSelectionMenu] =
      useState<LayerSelectionMenuData | null>(null);

    const hasActiveLayers = activeLayers.length > 0;

    const handlePopupClose = useCallback(() => {
      dispatch(hideMapClickInfo());
      setLayerSelectionMenu(null);
    }, [dispatch]);

    const handleLayerSelect = useCallback(
      async (selectedResult: LayerSelectionResult) => {
        if (!view) return;
        setLayerSelectionMenu(null);
        // Immediately show popover with loading state
        dispatch(
          showMapClickInfo(
            formatMapClickInfo(
              {
                mapPoint: {
                  latitude: selectedResult.latitude,
                  longitude: selectedResult.longitude,
                },
              },
              {
                layerName: selectedResult.data?.display_name,
                layerId: String(selectedResult?.data?.id || ''),
                attributes: {},
                isLoading: true, // Add loading flag
              },
            ),
          ),
        );

        const attributesBySource: Record<string, Record<string, unknown>[]> =
          {};

        for (const layer of activeLayers) {
          // Get all visible sublayers (or the layer itself if no sublayers)
          const queryableLayers = getQueryableLayers(layer.gisLayer);

          for (const queryableLayer of queryableLayers) {
            if (
              selectedResult.data.id === layer.service.id &&
              selectedResult.layerType === FEATURE_SERVICE &&
              layer.service.attributes?.gis_service_category === FEATURE_SERVICE
            ) {
              const queryResult = await queryFeatureLayer({
                layer: queryableLayer as FeatureLayer,
                longitude: selectedResult?.mapPoint?.longitude || 0,
                latitude: selectedResult?.mapPoint?.latitude || 0,
                fallbackLayerName: layer.title,
                bufferMeters: queryBufferMeters,
              });

              if (queryResult) {
                if (!attributesBySource[queryResult.sourceTitle]) {
                  attributesBySource[queryResult.sourceTitle] = [];
                }
                attributesBySource[queryResult.sourceTitle].push(
                  queryResult.attributes,
                );
              }
            } else if (
              selectedResult.data.id === layer.service.id &&
              selectedResult.layerType === MAP_SERVICE &&
              layer.service.attributes?.gis_service_category === MAP_SERVICE
            ) {
              const identifyResult: HelperFunctionResult | null =
                await identifyMapService({
                  url: layer.service.gis_service_url,
                  longitude: selectedResult.mapPoint.longitude,
                  latitude: selectedResult.mapPoint.latitude,
                  view,
                  token: layer.service?.token,
                  tolerance: identifyTolerance,
                  fallbackLayerName: layer.title,
                });

              if (identifyResult) {
                if (!attributesBySource[identifyResult.sourceTitle]) {
                  attributesBySource[identifyResult.sourceTitle] = [];
                }
                attributesBySource[identifyResult.sourceTitle].push(
                  identifyResult.attributes,
                );
              }
            }
          }
        }

        dispatch(
          showMapClickInfo(
            formatMapClickInfo(
              {
                mapPoint: {
                  latitude: selectedResult.latitude,
                  longitude: selectedResult.longitude,
                },
              },
              {
                layerName: selectedResult.data?.display_name,
                layerId: String(selectedResult.data.id),
                attributes: attributesBySource,
                isLoading: false, // Remove loading flag
              },
            ),
          ),
        );
      },
      [dispatch, activeLayers, queryBufferMeters, view, identifyTolerance],
    );

    const handleMapClick = useCallback(
      async (event: MapClickEvent) => {
        if (mapRef) mapRef.style.cursor = 'wait';

        try {
          const results: LayerSelectionResult[] = [];

          // Add active GIS services to the list of suggested results
          // this includes both basemap and supplemental service layers
          activeLayers.forEach(({ service }) => {
            results.push({
              layerName: service.display_name,
              layerId: String(service.id),
              feature: { attributes: {} },
              data: service,
              layerType: service.attributes?.gis_service_category || '',
              mapPoint: event?.mapPoint,
              latitude: event.mapPoint.latitude,
              longitude: event.mapPoint.longitude,
            });
          });

          // If result list has only one item, select it immediately
          if (results.length === 1) {
            handleLayerSelect({
              layerName: results[0].layerName,
              layerId: results[0].layerId,
              feature: { attributes: results[0].feature.attributes || {} },
              latitude: event.mapPoint.latitude,
              longitude: event.mapPoint.longitude,
              data: results[0].data,
              layerType: results[0].layerType,
              mapPoint: event?.mapPoint,
            });
          } else if (results.length > 1) {
            // If result list has multiple items, show the layer selection menu
            setLayerSelectionMenu({
              results: results.map(result => ({
                layerName: result.layerName,
                layerId: result.layerId,
                feature: { attributes: result.feature.attributes || {} },
                latitude: event.mapPoint.latitude,
                longitude: event.mapPoint.longitude,
                data: result.data,
                layerType: result.layerType,
                mapPoint: event?.mapPoint,
              })),
              screenPoint: { x: event.x, y: event.y },
            });
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
          // recover from failure
        } finally {
          if (mapRef) mapRef.style.cursor = 'auto';
        }
      },
      [mapRef, activeLayers, handleLayerSelect],
    );

    useEffect(() => {
      if (!hasActiveLayers || !view) return;

      const mapClickHandler = view.on('click', (event: MapClickEvent) => {
        if (event.button === 2) {
          // right click
          event.stopPropagation();
          event.preventDefault();
          if (mapClickInfo) {
            dispatch(hideMapClickInfo());
          }
          handleMapClick(event);
        } else {
          setLayerSelectionMenu(null);
          dispatch(hideMapClickInfo());
        }
      });

      return () => mapClickHandler?.remove();
    }, [hasActiveLayers, view, handleMapClick, dispatch, mapClickInfo]);

    useEffect(() => {
      if (!hasActiveLayers && layerSelectionMenu) {
        handlePopupClose();
      }
    }, [hasActiveLayers, layerSelectionMenu, handlePopupClose]);

    return layerSelectionMenu ? (
      <LayerSelectionMenu
        results={layerSelectionMenu.results}
        screenPoint={layerSelectionMenu.screenPoint}
        onLayerSelect={handleLayerSelect}
      />
    ) : null;
  },
);

export default ClickToIdentifyGL;
